import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAppContext } from '../context/AppContext'
import PieChartComponent from './PieChartComponent'
import AdminForm from './AdminForm'
import EditableGuestTable from './EditableGuestTable'
import VoiceTranscriptsCard from './VoiceTranscriptsCard'
import groupManager from '../utils/groupManager'
import '../styles/AdminDashboard.css'
import * as XLSX from 'xlsx'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000'

const AdminDashboard = () => {
  const { user } = useAppContext()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [guests, setGuests] = useState(1)
  const [file, setFile] = useState(null)
  const [isSending, setIsSending] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [uploadResult, setUploadResult] = useState(null)

  // Settings
  const [showEditModal, setShowEditModal] = useState(false)

  // Admin info for editing
  const [adminDetails, setAdminDetails] = useState(null)
  const [editLoading, setEditLoading] = useState(false)

  const [transportSummary, setTransportSummary] = useState({ tlv: 0, herz: 0 })
  const [statsRefreshKey, setStatsRefreshKey] = useState(0)
  const [accordionOpen, setAccordionOpen] = useState(false)
  const [activeCity, setActiveCity] = useState(null)
  const [transportList, setTransportList] = useState([])

  // Group management states
  const [groups, setGroups] = useState([])
  const [groupStats, setGroupStats] = useState({})
  const [newGroupName, setNewGroupName] = useState('')
  const [isAddingGroup, setIsAddingGroup] = useState(false)

  // WhatsApp template
  const [waTemplateName, setWaTemplateName] = useState('')
  const [waLanguageCode, setWaLanguageCode] = useState('en_US')

  useEffect(() => {
    if (user && user.token){
          fetchGuests()
    fetchAdminDetails()
    fetchTransportSummary()
    fetchGroups()
    }

  }, [user])

  useEffect(() => {
    if (!accordionOpen) return
    fetchTransportList(activeCity)
  }, [accordionOpen, activeCity])

  // Update groups when guests data changes
  useEffect(() => {
    if (guests && guests !== 1 && guests.length > 0) {
      fetchGroups()
    }
  }, [guests])

  /* ----------------------------------
     Export Table to Excel
  ------------------------------------*/
  const exportToExcel = (data, filename) => {
    const headers = Object.keys(data[0] || {})
    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
    XLSX.writeFile(workbook, `${filename}.xlsx`)
  }

  async function fetchTransportSummary() {
    try {
      const { data } = await axios.get(`${BASE_URL}/admin/transport-summary`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      setTransportSummary(data) // { tlv: 5, herz: 3 }
    } catch (err) {
      console.error('Failed to fetch transport summary', err)
    }
  }

  async function fetchTransportList(city) {
    try {
      const { data } = await axios.get(
        `${BASE_URL}/admin/transport-list?city=${city}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      )
      setTransportList(data) // array of guests
    } catch (err) {
      console.error('Failed to fetch transport list', err)
    }
  }

  /* ----------------------------------
     Fetch Guest List
  ------------------------------------*/
  async function fetchGuests() {
    try {
      const res = await axios.get(`${BASE_URL}/admin/guests`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      })
      setGuests(res.data)
      setLoading(false)
    } catch (err) {
      console.error(err)
      setLoading(false)
      
      // Check if the error is due to invalid or expired token
      if (err.response && err.response.data && err.response.data.error === "Invalid or expired token") {
        navigate('/admin-login')
        return
      }
    }
  }

  /* ----------------------------------
     Fetch Admin Details (for Editing)
  ------------------------------------*/
  async function fetchAdminDetails() {
    try {
      const res = await axios.get(`${BASE_URL}/admin/event-info`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      setAdminDetails(res.data)
      // Set default city with safety check
      if (res.data.transport_stops && res.data.transport_stops.length > 0) {
        setActiveCity(res.data.transport_stops[0].toLowerCase())
      }
    } catch (err) {
      console.error('Failed to fetch admin details', err)
    }
  }

  /* ----------------------------------
     Handle File Upload
  ------------------------------------*/
  async function handleFileUpload() {
    if (!file) return
    setUploadError('')
    setUploadResult(null)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const res = await axios.post(`${BASE_URL}/admin/upload-guests`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      setUploadResult(res.data)
      fetchGuests()
      setStatsRefreshKey(k => k + 1)
    } catch (err) {
      console.error(err)
      const msg = err.response?.data?.error || 'שגיאה בהעלאת הקובץ. נסה שוב.'
      setUploadError(msg)
    }
  }

  /* ----------------------------------
     Send Messages to Guests
  ------------------------------------*/
  const sendMessages = async (route, body = {}) => {
    if (!window.confirm('האם אתה בטוח שברצונך לשלוח הודעה לאורחים שלך?')) return
    setIsSending(route)
    try {
      await axios.post(
        `${BASE_URL}/admin/${route}`,
        body,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      )
      alert('ההודעות נשלחו!')
    } catch (error) {
      console.error('Error sending messages:', error)
      alert('Failed to send messages.')
    }
    setIsSending('')
  }

  /* ----------------------------------
   Download empty guest template
------------------------------------*/
  const downloadTemplateExcel = () => {
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.aoa_to_sheet([
      ['שם משפחה (אופציונלי)', 'שם פרטי', 'טלפון', 'קבוצה', 'צד'],
      ['כהן', 'יוסי', '0521234567', 'משפחה', 'חתן']
    ])
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Guests')
    XLSX.writeFile(workbook, 'GuestTemplate.xlsx')
  }

  /* ----------------------------------
     Close the Edit Modal
  ------------------------------------*/
  const closeEditModal = () => {
    setShowEditModal(false)
  }

  /* ----------------------------------
     Save Edited Admin Details
  ------------------------------------*/
  const handleSaveEdit = async (fields) => {
    setEditLoading(true)
    try {
      const formData = new FormData()

      // Append all text fields
      formData.append('wife_firstname', fields.wife_firstname)
      formData.append('wife_lastname', fields.wife_lastname)
      formData.append('husband_firstname', fields.husband_firstname)
      formData.append('husband_lastname', fields.husband_lastname)
      formData.append('email', fields.email)
      formData.append('phone_number', fields.phone_number)
      formData.append('event_date', fields.event_date)
      formData.append('event_address', fields.event_address)
      formData.append('new_password', fields.new_password)
      formData.append('old_password', fields.old_password)
      formData.append('rsvpText', fields.rsvp_text)
      formData.append('rsvpTextSecond', fields.rsvp_second_text)
      formData.append('afterWeddingText', fields.after_wedding_text)

      // Append image only if provided
      if (fields.invitationFile) {
        formData.append('invitation', fields.invitationFile)
      }

      await axios.put(`${BASE_URL}/admin/edit`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data',
        },
      })

      alert('Details updated successfully!')
      closeEditModal()
      fetchAdminDetails() // Refresh UI with updated data
    } catch (err) {
      console.error('Failed to update details', err)
      alert('Failed to update details')
    }
    setEditLoading(false)
  }

  /* ----------------------------------
     Fetch Groups and Group Statistics
  ------------------------------------*/
  async function fetchGroups() {
    try {
      if (!guests || guests === 1 || guests.length === 0) {
        // Still set the default groups even if no guests
        setGroups(groupManager.getAllGroups())
        setGroupStats({})
        return
      }
      
      // Update group manager with any new groups from guest list
      groupManager.updateFromGuestList(guests)
      
      // Get all available groups
      const allGroups = groupManager.getAllGroups()
      setGroups(allGroups)
      
      // Calculate group statistics
      const stats = {}
      allGroups.forEach(group => {
        const groupGuests = guests.filter(guest => guest.guest_group === group)
        const attending = groupGuests.filter(guest => guest.is_coming === 'yes').length
        const notAttending = groupGuests.filter(guest => guest.is_coming === 'no').length
        const unknown = groupGuests.filter(guest => guest.is_coming === "don't know" || guest.is_coming === "don’t know").length
        
        stats[group] = {
          total: groupGuests.length,
          attending,
          notAttending,
          unknown,
          totalGuests: groupGuests.reduce((sum, guest) => sum + (parseInt(guest.number_of_guests) || 0), 0)
        }
      })
      setGroupStats(stats)
    } catch (err) {
      console.error('Failed to fetch groups', err)
    }
  }

  // Add new group
  const addNewGroup = async () => {
    if (!newGroupName.trim()) return
    
    setIsAddingGroup(true)
    try {
      // Add the group to the group manager
      groupManager.addGroup(newGroupName.trim())
      
      // Update local state with all groups from group manager
      setGroups(groupManager.getAllGroups())
      
      // Refresh groups data and stats
      fetchGroups()
      
      setNewGroupName('')
      
      // Debug: Show that the group was saved
      console.log('Current groups in localStorage:', groupManager.getStoredData())
      
      // alert(`קבוצה "${newGroupName.trim()}" נוספה בהצלחה!`)
    } catch (err) {
      console.error('Failed to add group', err)
      alert('שגיאה בהוספת הקבוצה')
    }
    setIsAddingGroup(false)
  }

  // Debug function to show current group state
  const debugGroupsState = () => {
    groupManager.debugGroups()
  }

  // Get pastel color for group
  const getGroupColor = (group, index) => {
    return groupManager.getGroupColor(group)
  }

  if (!user) return <p>Please log in</p>

  return (
    <div className="admin-dashboard">
      {/* Top bar with username */}
      <div className="top-bar">
        <div className="top-bar-inner">
          <div className="top-bar-greeting">
            <span className="greeting-label">שלום,</span>
            <span className="greeting-name">{user.username}</span>
          </div>
          <div className="top-bar-right">
            <span className="top-bar-badge">🎊 לוח ניהול אירוע</span>
          </div>
        </div>
      </div>
      
      <div className="dashboard-grid">
        {(!guests || guests.length === 0) && (
          <div className="admin-dashboard-card full-width">
            <div>📝 עדכון רשימת אורחים</div>

          <div className="upload-container">
            <div className="upload-header">
              <h4>עדכון רשימת אורחים</h4>
            </div>

            <div className="upload-steps-guide">
              <button className="upload-step-item step-action" onClick={downloadTemplateExcel}>
                <span className="step-badge">1</span>
                <span className="step-text">הורד תבנית</span>
              </button>
              <span className="step-arrow">←</span>
              <div className="upload-step-item">
                <span className="step-badge">2</span>
                <span className="step-text">מלא את הקובץ</span>
              </div>
              <span className="step-arrow">←</span>
              <div className="upload-step-item">
                <span className="step-badge">3</span>
                <span className="step-text">העלה כאן</span>
              </div>
            </div>

            <button className="download-template-btn" onClick={downloadTemplateExcel}>
              <span className="download-btn-icon">⬇</span>
              <span className="download-btn-content">
                <span className="download-btn-label">הורד תבנית Excel</span>
                <span className="download-btn-sub">חובה לפני העלאה</span>
              </span>
            </button>

            <div className="upload-divider">
              <span>ואז העלה את הקובץ המלא</span>
            </div>

            <label htmlFor="file-upload" className="upload-drop-zone">
              <input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls,.numbers"
                onChange={(e) => { setFile(e.target.files[0]); setUploadError(''); setUploadResult(null) }}
                style={{ display: 'none' }}
              />
              {file ? (
                <div className="drop-zone-file-selected">
                  <span>✅</span>
                  <span className="file-name">{file.name}</span>
                  <span className="drop-zone-change">לחץ לשינוי</span>
                </div>
              ) : (
                <>
                  <span className="drop-zone-icon">📂</span>
                  <span className="drop-zone-label">לחץ לבחירת קובץ Excel</span>
                  <span className="drop-zone-hint">.xlsx / .xls / .numbers</span>
                </>
              )}
            </label>

            <div className="upload-actions">
              <button
                className={`upload-btn ${file ? 'enabled' : 'disabled'}`}
                onClick={handleFileUpload}
                disabled={!file}
              >
                העלה רשימה
              </button>
            </div>

            <details className="template-details">
              <summary>איזה עמודות צריכות להיות בקובץ?</summary>
              <div className="template-info">
                <ul>
                  <li><strong>שם משפחה:</strong> שם המשפחה של האורח</li>
                  <li><strong>שם פרטי:</strong> השם הפרטי של האורח</li>
                  <li><strong>טלפון:</strong> מספר הטלפון של האורח</li>
                  <li><strong>קבוצה:</strong> הקבוצה של האורח (למשל: משפחה, חברים, עבודה)</li>
                  <li><strong>צד:</strong> צד החתונה (חתן או כלה)</li>
                </ul>
                <p><strong>דוגמה:</strong> כהן | יוסי | 0521234567 | משפחה | חתן</p>
              </div>
            </details>

            {uploadError && (
              <div className="upload-feedback error">❌ {uploadError}</div>
            )}

            {uploadResult && (
              <div className="upload-feedback success">
                <div className="feedback-title">✅ {uploadResult.message}</div>
                {uploadResult.skipped > 0 && (
                  <div className="feedback-skipped">
                    <strong>⚠️ {uploadResult.skipped} שורות דולגו:</strong>
                    <ul>
                      {uploadResult.skippedRows.map((s, i) => (
                        <li key={i}>שורה {s.row}: {s.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <p className="upload-notes">
              <strong>שימו לב:</strong> הקובץ חייב להיות בפורמט Excel (.xlsx) או Numbers (.numbers) ולכלול את העמודות: שם משפחה, שם פרטי, טלפון
            </p>
          </div>
        </div>
        )}
        
        <div className="admin-dashboard-card">
          <div>📊 סיכום אישורי הגעה</div>
          <PieChartComponent refreshKey={statsRefreshKey} />
        </div>

          {adminDetails?.organize_transport && Object.values(transportSummary).some(v => v > 0) && (
        <div className="admin-dashboard-card">
          <div>🚌 מעוניינים בהסעה</div>

          <div className="transport-blocks">
            {(adminDetails?.transport_stops ?? []).map((stop, i) => {
              if (stop === "") return null; // Skip empty stops
              return (
              <div className="transport-box" key={i}>
                <h4>{stop}</h4>
                <p className="count">
                  {transportSummary[stop.toLowerCase()] || 0}
                </p>
              </div>
            )})}
          </div>

          <div className="transport-accordion">
            <div
              className={`accordion-header ${accordionOpen ? 'open' : ''}`}
              onClick={() => setAccordionOpen(!accordionOpen)}
            >
              <span className="accordion-icon">
                {accordionOpen ? '▲' : '▼'}
              </span>
              <span>הראה רשימה מלאה</span>
            </div>

            {accordionOpen && (
              <div className="accordion-content">
                <h3>רשימות הסעה</h3>

                {/* tabs */}
                <div className="transport-tabs">
                  {(adminDetails?.transport_stops ?? []).map((stop, i) => {
                    if (stop === "") return null; // Skip empty stops
                    return (
                    <button
                      key={i}
                      className={
                        activeCity === stop.toLowerCase() ? 'active' : ''
                      }
                      onClick={() => setActiveCity(stop.toLowerCase())}
                    >
                      {stop}
                    </button>
                  )})}
                </div>

                <button
                  className="export-btn"
                  onClick={() => exportToExcel(transportList, 'TransportList')}
                >
                  הורד אקסל
                </button>
                <table className="guest-table transport-table">
                  <thead>
                    <tr>
                      <th>שם</th>
                      <th>טלפון</th>
                      <th>מספר אורחים</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transportList.map((g, i) => (
                      <tr key={i}>
                        <td>
                          {g.firstname} {g.lastname}
                        </td>
                        <td>{g.phone_number}</td>
                        <td>{g.number_of_guests}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
        
        <div className="admin-dashboard-card guest-list-card">
          <div>👥 רשימת אורחים</div>
          <div>
            {loading ? (
              <p>⏳ טוען רשימת אורחים...</p>
            ) : guests !== 1 ? (
              <EditableGuestTable
                guests={guests}
                onGuestsUpdate={fetchGuests}
                sendMessages={sendMessages}
              />
            ) : (
              'לא נמצאו אורחים. אנא העלה רשימת אורחים.'
            )}
          </div>
        </div>

        {/* GROUPS MANAGEMENT SECTION */}
        {guests && guests !== 1 && guests.length > 0 && (
          <div className="admin-dashboard-card groups-management-card">
            <div className="groups-header">
              <span className="groups-icon">🏷️</span>
              <span>ניהול קבוצות אורחים</span>
            </div>
            
            <div className="groups-container">
              <div className="add-group-section">
                <h4>הוסף קבוצה חדשה</h4>
                <div className="add-group-form">
                  <input
                    type="text"
                    placeholder="שם הקבוצה החדשה"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addNewGroup()}
                    disabled={isAddingGroup}
                  />
                  <button
                    onClick={addNewGroup}
                    disabled={!newGroupName.trim() || isAddingGroup}
                    className="add-group-btn"
                  >
                    {isAddingGroup ? '⏳' : '➕'} הוסף
                  </button>
                </div>
              </div>

              <div className="groups-list">
                <h4>קבוצות קיימות</h4>
                {groups.length > 0 ? (
                  <div className="groups-badges">
                    {groups.map((group, index) => (
                      <div 
                        key={index} 
                        className="group-badge"
                        style={{ backgroundColor: getGroupColor(group, index) }}
                      >
                        <span className="group-name">{group}</span>
                        <span className="group-count">
                          {groupStats[group]?.total || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-groups-message">
                    אין קבוצות עדיין. הוסף קבוצה חדשה או עדכן את האורחים עם קבוצות.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
    
    {user.isAdmin && (
      <>
      
      {/* VOICE TRANSCRIPTS SECTION */}
      <VoiceTranscriptsCard />

      
      
      {/* SEND MESSAGE SECTION */}
      <div className="admin-dashboard-card">
        <div className="messages-section-header">📱 שליחת הודעות ידנית</div>

        <div className="messages-container">
          <div className="message-group">
            <div className="message-info">
              <h4>הודעת הזמנה ראשונה</h4>
              <p>שלח הזמנות לכל האורחים ברשימה</p>
            </div>
            <button
              className="message-btn primary"
              onClick={() => sendMessages('send-message')}
              disabled={isSending === 'send-message'}
            >
              {isSending === 'send-message' ? (
                <>
                  <span className="spinner"></span>
                  שולח...
                </>
              ) : (
                <>
                  <span className="btn-icon">📩</span>
                  שלח הודעות לאורחים
                </>
              )}
            </button>
          </div>

          <div className="message-group">
            <div className="message-info">
              <h4>הודעת אישורי הגעה למי שעדיין לא ענה</h4>
              <p>שלח תזכורות לאורחים שעדיין לא אישרו הגעה</p>
            </div>
            <button
              className="message-btn secondary"
              onClick={() => sendMessages('send-second-message')}
              disabled={isSending === 'send-second-message'}
            >
              {isSending === 'send-second-message' ? (
                <>
                  <span className="spinner"></span>
                  שולח...
                </>
              ) : (
                <>
                  <span className="btn-icon">🔔</span>
                שלח הודעה לאורחים שלא אישרו
                </>
              )}
            </button>
          </div>

          <div className="message-group">
            <div className="message-info">
              <h4>הודעת תזכורת יום לפני האירוע</h4>
              <p>שלח תזכורת שמחר זה קורה !</p>
            </div>
            <button
              className="message-btn purple"
              onClick={() => sendMessages('send-reminder-message')}
              disabled={isSending === 'send-reminder-message'}
            >
              {isSending === 'send-reminder-message' ? (
                <>
                  <span className="spinner"></span>
                  שולח...
                </>
              ) : (
                <>
                  <span className="btn-icon">⏰</span>
                  שלח תזכורת נוספת
                </>
              )}
            </button>
          </div>

          <div className="message-group">
            <div className="message-info">
              <h4>הודעת תודה</h4>
              <p>שלח הודעת תודה לאורחים שהגיעו לחתונה</p>
            </div>
            <button
              className="message-btn success"
              onClick={() => sendMessages('send-after-wedding-message')}
              disabled={isSending === 'send-after-wedding-message'}
            >
              {isSending === 'send-after-wedding-message' ? (
                <>
                  <span className="spinner"></span>
                  שולח...
                </>
              ) : (
                <>
                  <span className="btn-icon">🙏</span>
                  שלח תודה שבאתם לאורחים
                </>
              )}
            </button>
          </div>          </div>
        </div>

        {/* WHATSAPP TEMPLATE SECTION */}
        <div className="admin-dashboard-card">
          <div className="messages-section-header">💬 שליחת WhatsApp Template</div>
          <div className="messages-container">
            <div className="message-group">
              <div className="message-info">
                <h4>Template Name</h4>
                <input
                  type="text"
                  placeholder="e.g. laura_invitation"
                  value={waTemplateName}
                  onChange={(e) => setWaTemplateName(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #d1d5db', marginTop: '6px' }}
                />
              </div>
            </div>
            <div className="message-group">
              <div className="message-info">
                <h4>Language Code</h4>
                <input
                  type="text"
                  placeholder="e.g. en_US"
                  value={waLanguageCode}
                  onChange={(e) => setWaLanguageCode(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #d1d5db', marginTop: '6px' }}
                />
              </div>
            </div>
            <div className="message-group">
              <button
                className="message-btn primary"
                onClick={() => sendMessages('send-whatsapp', { templateName: waTemplateName, languageCode: waLanguageCode })}
                disabled={isSending === 'send-whatsapp' || !waTemplateName.trim() || !waLanguageCode.trim()}
              >
                {isSending === 'send-whatsapp' ? (
                  <>
                    <span className="spinner"></span>
                    שולח...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">💬</span>
                    שלח WhatsApp לכל האורחים
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        </>

      )}


      </div>
     
      {/* -------------------------------
          EDIT DETAILS MODAL
      -------------------------------- */}
      {showEditModal && adminDetails && (
        <div className="edit-modal">
          <div className="edit-modal-content">
            <h3>Edit Your Details</h3>
            <AdminForm
              mode="edit"
              initialData={adminDetails}
              onSubmit={handleSaveEdit}
              loading={editLoading}
            />
            <button className="cancel-button" onClick={closeEditModal}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard
